import { AutoAnnotation, AutoAnnotationGeometryEnum, ConfidenceReportRegionResponse, TaskDetail, CreateRectangleAnnotation, CreatePolygonAnnotation, CreateClassificationAnnotation, UpdateStagedAnnotation } from "@repo/schema";
import { Annotation } from "../types/annotations";
import { normalizeGroupOrder } from "./groupAnnotations";

export function taskDetailToAnnotations(detail: TaskDetail): Annotation[] {
  const annotations: Annotation[] = [];

  for (const rect of detail.rectangleAnnotations ?? []) {
    annotations.push({
      id: String(rect.id),
      apiId: rect.id,
      labelId: String(rect.label.id),
      labelName: rect.label.name,
      color: rect.label.color,
      type: "bbox",
      bbox: { x: rect.x, y: rect.y, width: rect.width, height: rect.height },
      groupId: rect.groupId ?? null,
    });
  }

  for (const poly of detail.polygonAnnotations ?? []) {
    annotations.push({
      id: String(poly.id),
      apiId: poly.id,
      labelId: String(poly.label.id),
      labelName: poly.label.name,
      color: poly.label.color,
      type: "polygon",
      points: poly.value,
      groupId: poly.groupId ?? null,
    });
  }

  for (const cls of detail.classificationAnnotations ?? []) {
    annotations.push({
      id: String(cls.id),
      apiId: cls.id,
      labelId: String(cls.label.id),
      labelName: cls.label.name,
      color: cls.label.color,
      type: "class",
    });
  }

  // Staged auto-annotations: predictions a run produced that nobody has
  // reviewed yet. They render like any other shape so the labeller can edit
  // them in place, but carry no `apiId` — absence of an id is what tells
  // `annotationsToUpdatePayload` to INSERT, so approving is what promotes them
  // into real annotations. A plain save keeps them staged instead; see
  // `splitStagedAnnotations`.
  for (const auto of detail.autoAnnotations ?? []) {
    const common = {
      id: stagedAnnotationId(auto.id),
      apiId: undefined,
      labelId: String(auto.label.id),
      labelName: auto.label.name,
      color: auto.label.color,
      groupId: null,
    };
    if (
      auto.geometry === AutoAnnotationGeometryEnum.RECTANGLE &&
      auto.x != null &&
      auto.y != null &&
      auto.width != null &&
      auto.height != null
    ) {
      annotations.push({
        ...common,
        type: "bbox",
        bbox: { x: auto.x, y: auto.y, width: auto.width, height: auto.height },
      });
    } else if (
      auto.geometry === AutoAnnotationGeometryEnum.POLYGON &&
      auto.value
    ) {
      annotations.push({ ...common, type: "polygon", points: auto.value });
    }
  }

  // Ensure group members are always contiguous in the local array regardless
  // of the order the server returns rows in (no order column exists in the DB).
  // This keeps the rest of the editing logic (drag-reorder, makeGroupContiguous)
  // consistent and prevents the sidebar from fracturing a group into singletons.
  return normalizeGroupOrder(annotations);
}

/** Canvas id prefix of a staged auto-annotation loaded from task detail. */
const STAGED_ID_PREFIX = "auto-";
/**
 * Canvas id prefix of a run's boxes placed straight from its response. The run
 * staged them server-side too; the task-detail refetch swaps them for
 * `auto-` shapes moments later.
 */
const PREDICTED_ID_PREFIX = "pred-";

/** Canvas id of the staged auto-annotation row `id`. */
export const stagedAnnotationId = (id: number): string =>
  `${STAGED_ID_PREFIX}${id}`;

/** Is this shape a run's box that has not been swapped for its staged row yet? */
export const isPredictedAnnotation = (a: Annotation): boolean =>
  a.id.startsWith(PREDICTED_ID_PREFIX);

/**
 * Which staged row each of the newest run's `pred-` shapes became.
 *
 * A lone run and the batch's run both stage the response's shapes in response
 * order, in one insert, and task detail lists staged rows by id — so the shape
 * the client numbered `pred-<stamp>-<idx>` is the idx-th staged row. The label
 * check is the safety net: a staged set from some other run will not line up,
 * and a shape that does not match simply keeps no history.
 */
export function mapPredictedToStaged(
  annotations: Annotation[],
  staged: AutoAnnotation[],
): Map<string, string> {
  const parsed = annotations.flatMap((a) => {
    const match = /^pred-(\d+)-(\d+)$/.exec(a.id);
    return match ? [{ a, stamp: Number(match[1]), idx: Number(match[2]) }] : [];
  });
  const newest = Math.max(...parsed.map((p) => p.stamp));
  const ids = new Map<string, string>();
  for (const { a, stamp, idx } of parsed) {
    const row = staged[idx];
    if (stamp !== newest || row === undefined) continue;
    if (String(row.label.id) !== a.labelId) continue;
    ids.set(a.id, stagedAnnotationId(row.id));
  }
  return ids;
}

/** Is this canvas id an auto-annotation still awaiting approval? */
export const isStagedId = (id: string): boolean =>
  id.startsWith(STAGED_ID_PREFIX) || id.startsWith(PREDICTED_ID_PREFIX);

/** Is this shape an auto-annotation still awaiting approval? */
export const isStagedAnnotation = (a: Annotation): boolean => isStagedId(a.id);

/**
 * Split the canvas for a plain (non-approving) save: the human annotations to
 * save, plus the staged boxes as edits to write back onto their staged rows.
 *
 * `stagedAnnotations` is undefined — "leave the staged set alone" — while any
 * staged box is a `pred-` shape with no staged row id to address. Sending the
 * rest would delete those rows as if the labeller had removed them.
 */
export function splitStagedAnnotations(annotations: Annotation[]): {
  saved: Annotation[];
  stagedAnnotations: UpdateStagedAnnotation[] | undefined;
} {
  const saved: Annotation[] = [];
  const staged: UpdateStagedAnnotation[] = [];
  let addressable = true;
  for (const a of annotations) {
    if (!isStagedAnnotation(a)) {
      saved.push(a);
      continue;
    }
    if (!a.id.startsWith(STAGED_ID_PREFIX)) {
      addressable = false;
      continue;
    }
    const id = Number(a.id.slice(STAGED_ID_PREFIX.length));
    const labelId = Number(a.labelId);
    if (a.type === "bbox" && a.bbox) {
      staged.push({ id, labelId, ...a.bbox });
    } else if (a.type === "polygon" && a.points) {
      staged.push({ id, labelId, value: a.points });
    }
  }
  return { saved, stagedAnnotations: addressable ? staged : undefined };
}

/** Prefix distinguishing inferred-region annotation ids from real annotation ids. */
export const INFERRED_ID_PREFIX = "inferred-";

/** Canvas annotation id for an inferred confidence-report region. */
export const inferredAnnotationId = (regionId: number) =>
  `${INFERRED_ID_PREFIX}${regionId}`;

/**
 * Convert inferred regions from a confidence report into the `Annotation` shape
 * used by the canvas and sidebar. These are read-only display-only annotations:
 * - `id` uses an `inferred-` prefix to avoid collisions with real annotation ids.
 * - `apiId` is left undefined so they are never sent in a save payload.
 * - `inferred = true` and `score` are set for canvas styling.
 */
export function regionsToAnnotations(
  regions: ConfidenceReportRegionResponse[],
): Annotation[] {
  return regions.map((r, i): Annotation => {
    const base: Annotation = {
      id: inferredAnnotationId(r.id),
      labelId: String(r.label.id),
      labelName: r.label.name,
      color: r.label.color,
      type: r.geometry === "RECTANGLE" ? "bbox" : "polygon",
      score: r.score,
      inferred: true,
      displayId: i + 1,
    };
    if (r.geometry === "RECTANGLE" && r.x != null && r.y != null && r.width != null && r.height != null) {
      base.bbox = { x: r.x, y: r.y, width: r.width, height: r.height };
    } else if (r.geometry === "POLYGON" && r.value) {
      base.points = r.value as [number, number][];
    }
    return base;
  });
}

/**
 * Bounding-box view of a polygon annotation — mirrors how the confidence-report
 * job evaluates polygon GT against a detection model (each polygon becomes its
 * vertex bounding box; polygons with fewer than 3 points are not evaluated).
 * Non-polygon annotations pass through unchanged.
 */
export function polygonAnnotationToBbox(a: Annotation): Annotation {
  if (a.type !== "polygon" || !a.points || a.points.length < 3) return a;
  const xs = a.points.map((p) => p[0]);
  const ys = a.points.map((p) => p[1]);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return {
    ...a,
    type: "bbox",
    bbox: { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y },
    points: undefined,
  };
}

export function annotationsToUpdatePayload(annotations: Annotation[]): {
  rectangleAnnotations: CreateRectangleAnnotation[];
  polygonAnnotations: CreatePolygonAnnotation[];
  classificationAnnotations: CreateClassificationAnnotation[];
} {
  const rectangleAnnotations: CreateRectangleAnnotation[] = [];
  const polygonAnnotations: CreatePolygonAnnotation[] = [];
  const classificationAnnotations: CreateClassificationAnnotation[] = [];

  for (const a of annotations) {
    const labelId = Number(a.labelId);
    if (a.type === "bbox" && a.bbox) {
      rectangleAnnotations.push({
        ...(a.apiId != null && { id: a.apiId }),
        labelId,
        x: a.bbox.x,
        y: a.bbox.y,
        width: a.bbox.width,
        height: a.bbox.height,
        ...(a.groupId != null && { groupId: a.groupId }),
      });
    } else if (a.type === "polygon" && a.points) {
      polygonAnnotations.push({
        ...(a.apiId != null && { id: a.apiId }),
        labelId,
        value: a.points,
        ...(a.groupId != null && { groupId: a.groupId }),
      });
    } else if (a.type === "class" && a.labelId) {
      classificationAnnotations.push({
        ...(a.apiId != null && { id: a.apiId }),
        labelId,
      });
    }
  }

  return { rectangleAnnotations, polygonAnnotations, classificationAnnotations };
}
